import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { API, apiFetch } from '../api';
import './ModalTheme.css';
import { coveredAssetIds } from './helpers';

const EXTERIOR_TO_CONDITION = {
  'Factory New':   'Factory New',
  'Minimal Wear':  'Minimal Wear',
  'Field-Tested':  'Field Tested',
  'Well-Worn':     'Well Worn',
  'Battle-Scarred':'Battle Scarred',
};

// ── Trade-history matching ────────────────────────────────
// Given an item that left inventory (change) and a list of Steam trade events,
// find the best matching event where the item appears in itemsGiven.
function findTradeMatch(change, tradeEvents) {
  if (!tradeEvents?.length) return null;
  const baseName = stripCondition(change.market_hash_name || '');
  const condVal  = EXTERIOR_TO_CONDITION[change.exterior] || null;

  for (const event of tradeEvents) {
    if (event.direction !== 'TRADE' && event.direction !== 'LIKELY_SALE_OR_TRANSFER') continue;
    for (const given of (event.itemsGiven || [])) {
      const givenBase = stripCondition(given.name || '');
      const givenCond = given.condition ? (EXTERIOR_TO_CONDITION[given.condition] || given.condition) : null;
      const nameOk = givenBase === baseName;
      const condOk = !condVal || !givenCond || condVal === givenCond;
      if (nameOk && condOk) return event;
    }
  }
  return null;
}

// Convert a trade-history received item → ChangeCard receivedItems format
function tradeItemToReceived(r, currentInventoryItems = []) {
  const name    = stripCondition(r.name || '');
  const cond    = r.condition ? (EXTERIOR_TO_CONDITION[r.condition] || r.condition) : null;
  const invMatch = r.assetId
    ? currentInventoryItems.find(i => i.assetId === r.assetId)
    : currentInventoryItems.find(i =>
        stripCondition(i.market_hash_name || '') === name &&
        (EXTERIOR_TO_CONDITION[i.exterior] || null) === cond
      );
  return {
    name,
    condition: cond,
    assetId:   r.assetId || invMatch?.assetId || null,
    icon_url:  invMatch?.icon_url || null,
    _autoLinked: true,
  };
}
const COND_PILLS = ['Factory New','Minimal Wear','Field Tested','Well Worn','Battle Scarred'];

function stripCondition(name) {
  return name.replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
}
function createEmptyRow(settings) {
  return { skin: null, description: '', condition: null, itemType: null, buyPrice: '', sellPrice: '',
    siteFee: settings?.sites?.[0] || null, profit: '', timestamp: new Date().toISOString() };
}

// ── Minimal inline autocomplete ───────────────────────────
function ItemSearch({ searchableOptions = [], value, onChange, placeholder }) {
  const [q, setQ]           = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen]     = useState(false);
  const ref = useRef(null);

  const search = v => {
    if (!v.trim()) { setResults([]); setOpen(false); return; }
    const words = v.toLowerCase().split(/\s+/);
    const out = [];
    for (const opt of searchableOptions) {
      if (out.length >= 10) break;
      if (words.every(w => opt.value.toLowerCase().includes(w))) out.push(opt);
    }
    setResults(out); setOpen(out.length > 0);
  };

  return (
    <div className="iim-ac" ref={ref}>
      <input
        type="text" className="mdl-input" value={q}
        onChange={e => { setQ(e.target.value); search(e.target.value); onChange(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder={placeholder || 'Search item name…'}
      />
      {open && (
        <div className="iim-ac-list">
          {results.map(opt => (
            <div key={opt.value} className="iim-ac-opt"
              onMouseDown={() => { setQ(opt.value); onChange(opt.value); setOpen(false); }}>
              {opt.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ChangeCard — one card per disappeared/resolved item ───
function ChangeCard({ change, action, onAction, settings, searchableOptions, inventoryItems = [] }) {
  const [addMode, setAddMode]     = useState('inventory'); // 'inventory' | 'manual'
  const [invSearch, setInvSearch] = useState('');
  const [stageName, setStageName] = useState('');
  const [stageCond, setStageCond] = useState(null);

  const key        = change.assetId || change.id;
  const act        = action?.action;
  const isResolved = change._type === 'tradeResolved';
  const baseName   = isResolved ? change.expectedName : stripCondition(change.market_hash_name || '');
  const condVal    = isResolved ? change.expectedCondition : (EXTERIOR_TO_CONDITION[change.exterior] || null);
  const receivedItems = action?.receivedItems || [];

  const addFromInventory = item => {
    const name = stripCondition(item.market_hash_name);
    const cond = EXTERIOR_TO_CONDITION[item.exterior] || null;
    const already = receivedItems.some(r => r.assetId === item.assetId);
    if (already) return;
    onAction(key, { receivedItems: [...receivedItems, { name, condition: cond, assetId: item.assetId, icon_url: item.icon_url }] });
  };

  const addManual = () => {
    if (!stageName.trim()) return;
    onAction(key, { receivedItems: [...receivedItems, { name: stageName.trim(), condition: stageCond, assetId: null }] });
    setStageName(''); setStageCond(null);
  };

  const removeReceived = idx => {
    onAction(key, { receivedItems: receivedItems.filter((_, i) => i !== idx) });
  };

  const filteredInv = inventoryItems.filter(i =>
    i.assetId !== change.assetId &&
    (!invSearch.trim() || i.market_hash_name.toLowerCase().includes(invSearch.toLowerCase()))
  );

  return (
    <div className="iim-change">
      {/* Item row */}
      <div className="iim-change-top">
        {change.icon_url
          ? <img src={change.icon_url} alt="" className="iim-change-img" />
          : <div className="iim-change-img iim-img-blank" />
        }
        <div className="iim-change-txt">
          <div className="iim-change-name">{baseName}</div>
          <div className="iim-change-cond">
            {condVal || 'No condition'}
            {isResolved && <span className="iim-change-flag">· Trade resolved</span>}
          </div>
        </div>
        {/* Action buttons */}
        <div className="iim-acts">
          {isResolved ? (
            <>
              <ActionBtn active={act === 'confirm'} tone="good"
                onClick={() => onAction(key, { action: 'confirm' })}>Confirm</ActionBtn>
              <ActionBtn active={act === 'skip'} tone="mute"
                onClick={() => onAction(key, { action: 'skip' })}>Skip</ActionBtn>
            </>
          ) : (
            <>
              <ActionBtn active={act === 'sold'} tone="warn"
                onClick={() => onAction(key, { action: 'sold' })}>Sold</ActionBtn>
              <ActionBtn active={act === 'traded'} tone="info"
                onClick={() => onAction(key, { action: 'traded', receivedItems: [] })}>Traded</ActionBtn>
              <ActionBtn active={act === 'skip'} tone="mute"
                onClick={() => onAction(key, { action: 'skip' })}>Skip</ActionBtn>
            </>
          )}
        </div>
      </div>

      {/* Proven sale. Not a guess like the trade-history banner below: the
          server matched this exact item to a settled CSFloat sale, so the
          platform and price are already filled in and all that is missing is
          what you paid for it. */}
      {action?._csfloatSale && (
        <div className="iim-detect sale">
          <div className="iim-detect-txt">
            <div className="iim-detect-title">
              Confirmed sold on CSFloat
              {action._csfloatSale.soldAt && (
                <span className="iim-detect-when">
                  · {new Date(action._csfloatSale.soldAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="iim-detect-sub">
              Sold for ${Number(action._csfloatSale.price).toFixed(2)}. This item was never
              tracked, so add what you paid to record the profit.
            </div>
          </div>
        </div>
      )}

      {/* Trade detected banner */}
      {action?._matchEvent && (
        <div className={`iim-detect${action._suggestSold ? ' sale' : ''}`}>
          <div className="iim-detect-txt">
            <div className="iim-detect-title">
              {action._suggestSold ? 'Possible sale detected' : 'Trade auto-detected from history'}
              {action._matchEvent.timestamp && (
                <span className="iim-detect-when">
                  · {new Date(action._matchEvent.timestamp).toLocaleDateString()}
                </span>
              )}
            </div>
            {!action._suggestSold && action._matchEvent.itemsReceived?.length > 0 && (
              <div className="iim-detect-sub">
                Received: {action._matchEvent.itemsReceived.map(r => stripCondition(r.name || r.name)).join(', ')}
              </div>
            )}
          </div>
          {action._suggestSold && (
            <button className="mdl-pill on warn"
              onClick={() => onAction(key, { ...action, action: 'sold' })}>Mark sold</button>
          )}
        </div>
      )}

      {/* Sold form */}
      {act === 'sold' && (
        <div className="iim-form split">
          <div>
            <span className="mdl-label">Platform</span>
            <select className="mdl-input" style={{ appearance: 'auto' }}
              value={action.platform || ''} onChange={e => onAction(key, { platform: e.target.value })}>
              <option value="">Select…</option>
              {(settings?.sites || []).map(s => <option key={s.value} value={s.value}>{s.label?.split(' ')[0]}</option>)}
            </select>
          </div>
          <div>
            <span className="mdl-label">Sell Price ($)</span>
            <input type="number" className="mdl-input" step="0.01" min="0" placeholder="0.00"
              value={action.price || ''} onChange={e => onAction(key, { price: e.target.value })} />
          </div>
        </div>
      )}

      {/* Traded form */}
      {act === 'traded' && (
        <div className="iim-form stack">
          <div className="iim-form-title">What did you receive?</div>

          {/* Committed received items */}
          {receivedItems.length > 0 && (
            <div className="iim-recv">
              {receivedItems.map((item, i) => (
                <div key={i} className="iim-recv-line">
                  {item.icon_url && <img src={item.icon_url} alt="" className="iim-recv-img" />}
                  <span className="iim-recv-name">
                    {item.name}{item.condition ? ` (${item.condition})` : ''}
                    {item._autoLinked && <span className="iim-auto">Auto</span>}
                  </span>
                  <button className="iim-recv-x" onClick={() => removeReceived(i)}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* Add mode toggle */}
          <div className="iim-acts">
            {['inventory', 'manual'].map(m => (
              <button key={m} className={`mdl-pill${addMode === m ? ' on info' : ''}`}
                onClick={() => setAddMode(m)}>
                {m === 'inventory' ? 'From inventory' : 'Manual entry'}
              </button>
            ))}
          </div>

          {/* From inventory */}
          {addMode === 'inventory' && (
            <div>
              <input className="mdl-input" value={invSearch} onChange={e => setInvSearch(e.target.value)}
                placeholder="Search current inventory…" style={{ marginBottom: 8 }} />
              {filteredInv.length === 0 ? (
                <div className="iim-hint" style={{ textAlign: 'center', display: 'block', padding: '8px 0' }}>
                  {inventoryItems.length === 0 ? 'No inventory loaded' : 'No items match'}
                </div>
              ) : (
                <div className="iim-grid small">
                  {filteredInv.map(item => {
                    const selected = receivedItems.some(r => r.assetId === item.assetId);
                    return (
                      <div key={item.assetId} className={`iim-tile${selected ? ' on taken' : ''}`}
                        onClick={() => addFromInventory(item)}>
                        <div className="iim-img">
                          {item.icon_url
                            ? <img src={item.icon_url} alt="" loading="lazy" />
                            : <div className="iim-img-blank" />
                          }
                        </div>
                        <div className="iim-info">
                          <div className="iim-name">{stripCondition(item.market_hash_name)}</div>
                          {item.exterior && <div className="iim-ext">{item.exterior}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Manual entry */}
          {addMode === 'manual' && (
            <div className="mdl-stack">
              <ItemSearch
                searchableOptions={searchableOptions}
                value={stageName}
                onChange={v => setStageName(v)}
                placeholder="Search item name…"
              />
              <div className="iim-acts">
                {COND_PILLS.map(c => (
                  <button key={c} className={`mdl-pill${stageCond === c ? ' on info' : ''}`}
                    onClick={() => setStageCond(stageCond === c ? null : c)}>{c}</button>
                ))}
              </div>
              <div>
                <button className="mdl-btn primary" onClick={addManual} disabled={!stageName.trim()}>
                  + Add item
                </button>
              </div>
            </div>
          )}

          {receivedItems.length === 0 && (
            <div className="iim-hint">
              Leave empty if you didn't receive anything (gift trade / scam / etc.)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Tone maps to a colour in ModalTheme.css so both skins can pick their own:
// 'good' green, 'warn' amber, 'info' indigo, 'mute' grey.
function ActionBtn({ active, tone, onClick, children }) {
  return (
    <button className={`mdl-pill${active ? ` on ${tone}` : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

// ── Fetch floats button (sequential, rate-limited) ────────
// Only fetches items that are selected, have an inspect link, and are missing float data.
function FetchFloatsButton({ items, selected, onFloatsFetched }) {
  const [status, setStatus] = useState('idle'); // 'idle'|'running'|'done'
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const missingCount = items.filter(i => selected.has(i.assetId) && i.inspectLink && i.float == null).length;

  const fetchFloats = async () => {
    const targets = items.filter(i => selected.has(i.assetId) && i.inspectLink && i.float == null);
    if (!targets.length) return;
    setStatus('running');
    setProgress({ done: 0, total: targets.length });
    const results = {};
    for (const item of targets) {
      try {
        const res  = await fetch(`${API}/api/inventory/float?url=${encodeURIComponent(item.inspectLink)}`);
        const data = await res.json();
        if (data.float != null) results[item.assetId] = { float: data.float, paintSeed: data.paintSeed };
      } catch {}
      setProgress(p => ({ ...p, done: p.done + 1 }));
      await new Promise(r => setTimeout(r, 1200)); // respect CSFloat rate limit
    }
    onFloatsFetched(results);
    setStatus('done');
  };

  if (status === 'running') {
    return <span className="iim-count">Fetching floats {progress.done}/{progress.total}…</span>;
  }

  if (missingCount === 0) {
    const selWithFloat = items.filter(i => selected.has(i.assetId) && i.float != null).length;
    if (selWithFloat === 0) return null;
    return (
      <span className="iim-count mdl-ok">
        {selWithFloat} float{selWithFloat !== 1 ? 's' : ''} ready
      </span>
    );
  }

  return (
    <button className={`mdl-pill${status === 'done' ? ' on' : ''}`} onClick={fetchFloats}
      title={`Fetch float values for ${missingCount} item(s) missing float data via CSFloat API`}>
      {status === 'done' ? 'Floats fetched' : `Fetch Floats (${missingCount} missing)`}
    </button>
  );
}

// ── Main modal ────────────────────────────────────────────
export default function InventoryImportModal({
  onClose, onImport, settings, username, rows,
  livePrices, csfloatPrices, searchableOptions = [],
  onSteamIdSaved, onSellItem, onTradeItem, onReconcileSales, tradeHistory = [],
  variant = 'classic',
}) {
  const [view, setView]             = useState('idle');
  const [steamId, setSteamId]       = useState('');
  const [priceSource, setPriceSource] = useState('buff');
  const [items, setItems]           = useState([]);
  const [selected, setSelected]     = useState(new Set());
  const [changes, setChanges]       = useState([]);
  const [changeActions, setChangeActions] = useState({});
  const [pendingTrades, setPendingTrades] = useState([]);
  const [search, setSearch]         = useState('');
  const [filterExt, setFilterExt]   = useState('');
  const [error, setError]           = useState('');
  const [meta, setMeta]             = useState(null);
  const [floatOverrides, setFloatOverrides] = useState({}); // { assetId: { float, paintSeed } }

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    setSteamId(settings?.steamId || localStorage.getItem('cs2tracker_steamId') || '');
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', esc); };
  }, [onClose, settings?.steamId]);

  // ── Duplicate detection ───────────────────────────────
  // An asset id on a row is proof that copy is tracked. It is not the only
  // proof: a trade puts what it received straight into Live with no asset id at
  // all, and importing that item again is how one item became two rows. So the
  // same counting rule the Locker uses decides it here — see coveredAssetIds.
  const trackedAssetIds = useMemo(() => coveredAssetIds(rows, items), [rows, items]);

  const isTracked = item => trackedAssetIds.has(item.assetId);

  // ── Price lookup ──────────────────────────────────────
  const getPrice = useCallback(item => {
    if (priceSource === 'buff')    return livePrices?.[item.market_hash_name]?.starting_at?.price ?? null;
    if (priceSource === 'csfloat') return csfloatPrices?.[item.market_hash_name]?.price ?? null;
    return null;
  }, [priceSource, livePrices, csfloatPrices]);

  // ── Filtered grid items ───────────────────────────────
  const filteredItems = useMemo(() => {
    let r = items;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(i => i.market_hash_name.toLowerCase().includes(q));
    }
    if (filterExt) r = r.filter(i => i.exterior === filterExt);
    return r;
  }, [items, search, filterExt]);

  const availableExteriors = useMemo(() =>
    [...new Set(items.map(i => i.exterior).filter(Boolean))].sort()
  , [items]);

  // ── Fetch ─────────────────────────────────────────────
  const doFetch = async (force = false) => {
    const id = steamId.trim();
    if (!/^\d{17}$/.test(id)) { setError('Enter a valid 17-digit Steam64 ID'); return; }
    setView('loading'); setError('');

    try {
      const [invRes, snapRes] = await Promise.all([
        fetch(`${API}/api/inventory/${id}${force ? '?force=true' : ''}`),
        apiFetch(`/api/tracker/inventory/${username}`),
      ]);
      const invData  = await invRes.json();
      const snapData = await snapRes.json();
      if (!invRes.ok) throw new Error(invData.error || 'Failed to fetch inventory');

      const newItems   = invData.items || [];
      const baseline   = snapData.items || [];
      const pendTrades = snapData.pendingTrades || [];
      const newIds     = new Set(newItems.map(i => i.assetId));

      // Settle everything provable before asking anything.
      //
      // This inventory read is fresher than the snapshot the server holds, so
      // it is handed over: an item sold on CSFloat that went missing in THIS
      // fetch is proved and filed on this pass, instead of the user being asked
      // about it now and it being auto-resolved on the next sync. Every
      // question left below is one that genuinely needs a human.
      let reconciled = null;
      try {
        reconciled = await onReconcileSales?.(username, [...newIds].filter(Boolean));
      } catch { /* leave it as a question — the manual flow still works */ }
      const autoResolved = new Set(reconciled?.resolvedAssetIds || []);
      // Proven sold but never tracked: still a card, since only the user knows
      // the cost basis, but one that arrives with the answer already in it.
      const provenSales = new Map(
        (reconciled?.unmatched || []).map(u => [u.assetId, u])
      );

      // Disappeared items from current diff
      const disappeared = baseline
        .filter(i => !newIds.has(i.assetId) && !autoResolved.has(i.assetId))
        .map(i => ({ ...i, _type: 'disappeared' }));
      console.log(`[modal] Snapshot diff: ${baseline.length} baseline, ${newItems.length} current, ${disappeared.length} disappeared`);
      disappeared.forEach(i => console.log(`[modal]   disappeared: ${i.market_hash_name} (assetId=${i.assetId})`));

      // Pending disappearances from previous extension syncs that weren't resolved yet
      let pendingDisappeared = [];
      try {
        const pendRes = await apiFetch(`/api/tracker/pending-disappearances/${username}`);
        if (pendRes.ok) {
          const pendData = await pendRes.json();
          const alreadyShown = new Set(disappeared.map(i => i.assetId).filter(Boolean));
          // Also skip items that are now back in inventory, and ones the
          // reconciler just filed as sold.
          pendingDisappeared = (pendData.items || [])
            .filter(i => i.assetId && !alreadyShown.has(i.assetId) && !newIds.has(i.assetId)
              && !autoResolved.has(i.assetId))
            .map(i => ({ ...i, _type: 'disappeared', _fromPending: true }));
          console.log(`[modal] Pending disappearances from server: ${pendData.items?.length ?? 0} total, ${pendingDisappeared.length} new to show`);
          pendingDisappeared.forEach(i => console.log(`[modal]   pending: ${i.market_hash_name} (assetId=${i.assetId}, disappearedAt=${i._disappearedAt})`));
        } else {
          console.warn(`[modal] Failed to fetch pending disappearances: HTTP ${pendRes.status}`);
        }
      } catch (e) {
        console.error('[modal] Error fetching pending disappearances:', e);
      }

      // Pending trade resolutions (>7 days old, item now in inventory)
      const sevenDaysAgo  = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const resolvedTrades = pendTrades
        .filter(pt => pt.status === 'pending' && new Date(pt.tradedAt).getTime() <= sevenDaysAgo)
        .filter(pt => newItems.some(i =>
          stripCondition(i.market_hash_name) === pt.expectedName &&
          (EXTERIOR_TO_CONDITION[i.exterior] || '') === (pt.expectedCondition || '')
        ))
        .map(pt => ({ ...pt, _type: 'tradeResolved' }));

      const allChanges = [...disappeared, ...pendingDisappeared, ...resolvedTrades];
      console.log(`[modal] Total changes to show: ${allChanges.length} (${disappeared.length} current diff, ${pendingDisappeared.length} pending, ${resolvedTrades.length} trade resolutions)`);

      if (id !== (settings?.steamId || '')) onSteamIdSaved?.(id);
      localStorage.setItem('cs2tracker_steamId', id);

      setItems(newItems);
      setPendingTrades(pendTrades);
      setMeta({ cached: invData.cached, syncedAt: invData.syncedAt, totalCount: invData.totalCount });

      if (allChanges.length > 0) {
        // Don't update snapshot yet — wait until user resolves the changes
        // so a background extension sync can't erase unresolved disappearances
        setChanges(allChanges);
        // Pre-fill actions from trade history where possible
        const init = {};
        allChanges.forEach(c => {
          const key = c.assetId || c.id;
          if (c._type === 'tradeResolved') { init[key] = { action: null }; return; }
          // A settled CSFloat sale beats anything inferred from Steam history:
          // Steam only says the item moved, CSFloat says what it sold for.
          const proven = provenSales.get(c.assetId);
          if (proven) {
            init[key] = {
              action:   'sold',
              platform: 'csfloat',
              price:    Number(proven.price).toFixed(2),
              _csfloatSale: proven,
            };
            return;
          }
          const match = findTradeMatch(c, tradeHistory);
          if (match && match.direction === 'TRADE') {
            init[key] = {
              action:        'traded',
              receivedItems: (match.itemsReceived || []).map(r => tradeItemToReceived(r, newItems)),
              _matchEvent:   match,
            };
          } else if (match && match.direction === 'LIKELY_SALE_OR_TRANSFER') {
            init[key] = { action: null, _matchEvent: match, _suggestSold: true };
          } else {
            init[key] = { action: null };
          }
        });
        setChangeActions(init);
        setView('changes');
      } else {
        // No unresolved changes — safe to update snapshot now
        apiFetch(`/api/tracker/inventory/${username}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: newItems, steamId: id }),
        }).catch(() => {});
        setSelected(new Set());
        setView('grid');
      }
    } catch (e) {
      setError(e.message);
      setView('idle');
    }
  };

  // ── Confirm changes ────────────────────────────────────
  const confirmChanges = async () => {
    const resolvedIds = new Set();
    const resolvedDisappearanceAssetIds = [];

    console.log(`[modal] confirmChanges: processing ${changes.length} change(s)`);

    for (const change of changes) {
      const key = change.assetId || change.id;
      const action = changeActions[key] || {};
      if (!action.action || action.action === 'skip') {
        console.log(`[modal]   skip: ${change.market_hash_name || change.expectedName} (action=${action.action || 'none'})`);
        continue;
      }

      if (change._type === 'disappeared') {
        if (change.assetId) resolvedDisappearanceAssetIds.push(change.assetId);

        if (action.action === 'sold') {
          console.log(`[modal]   sold: ${change.market_hash_name} for $${action.price} on ${action.platform}`);
          onSellItem?.(
            stripCondition(change.market_hash_name),
            EXTERIOR_TO_CONDITION[change.exterior] || null,
            action.platform || null,
            parseFloat(action.price || 0),
            // The full change object, so handleSellItem can find or build the
            // row. When the server proved this sale, its receipt rides along:
            // without it the row would carry no record of which CSFloat trade
            // it settled, and the reconciler could offer the same sale again.
            action._csfloatSale ? { ...change, _csfloatSale: action._csfloatSale } : change,
          );
        } else if (action.action === 'traded') {
          const received = action.receivedItems || [];
          console.log(`[modal]   traded: ${change.market_hash_name} → ${received.length} received item(s), tradedWith=${action._matchEvent?.tradedWith || null}`);
          received.forEach(r => console.log(`[modal]     received: ${r.name} ${r.condition || ''} (assetId=${r.assetId})`));
          // Call once for the entire trade — handleTradeItem handles all received items
          onTradeItem?.({
            offeredItem:  change,
            tradedAt:     new Date().toISOString(),
            allReceived:  received,
            tradedWith:   action._matchEvent?.tradedWith || null,
          });
        }
      } else if (change._type === 'tradeResolved' && action.action === 'confirm') {
        console.log(`[modal]   tradeResolved confirm: ${change.expectedName}`);
        onTradeItem?.({ offeredItem: null, receivedName: change.expectedName,
          receivedCondition: change.expectedCondition, isInInventory: true,
          pendingTradeId: change.id, tradedAt: change.tradedAt });
        resolvedIds.add(change.id);
      }
    }

    if (resolvedIds.size > 0) {
      const remaining = pendingTrades.filter(pt => !resolvedIds.has(pt.id));
      apiFetch(`/api/tracker/pending-trades/${username}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingTrades: remaining }),
      }).catch(() => {});
    }

    // Clear resolved pending disappearances from the server queue
    if (resolvedDisappearanceAssetIds.length > 0) {
      apiFetch(`/api/tracker/pending-disappearances-resolve/${username}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetIds: resolvedDisappearanceAssetIds }),
      }).catch(() => {});
    }

    // Update the snapshot to current inventory now that changes are resolved
    apiFetch(`/api/tracker/inventory/${username}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, steamId: steamId.trim() }),
    }).catch(() => {});

    setSelected(new Set());
    setView('grid');
  };

  // ── Import ─────────────────────────────────────────────
  const handleImport = () => {
    const platformSite = priceSource === 'buff'
      ? settings?.sites?.find(s => s.value === 'buff')
      : priceSource === 'csfloat'
      ? settings?.sites?.find(s => s.value === 'csfloat')
      : null;

    // isTracked as well as selected: Select All takes the whole filtered grid,
    // and a tracked tile that is merely un-clickable would still ride along.
    const toImport = items
      .filter(i => selected.has(i.assetId) && !isTracked(i))
      .map(i => {
        const baseName   = stripCondition(i.market_hash_name);
        const condVal    = EXTERIOR_TO_CONDITION[i.exterior] || null;
        const price      = getPrice(i);
        const stickerDesc = (i.stickers || []).filter(s => s.name).map(s => s.name).join(', ');
        return {
          ...createEmptyRow(settings),
          skin:        { value: baseName, label: baseName },
          condition:   condVal ? { value: condVal, label: condVal } : null,
          assetId:     i.assetId,
          // Steam's own render, kept on the row so items too new to be in the
          // skins database still show a picture in the tracker.
          iconUrl:     i.icon_url || i.iconUrl || null,
          inspectLink: i.inspectLink || '',
          float:       floatOverrides[i.assetId]?.float ?? i.float ?? null,
          paintSeed:   floatOverrides[i.assetId]?.paintSeed ?? i.paintSeed ?? null,
          description: stickerDesc,
          stickers:    (i.stickers || []).filter(s => s.name),
          // Names arrive in market form ("Charm | Backsplash"), which is what
          // the strip prices them by. Kept on the row so it still says what
          // was on the item long after the item has been sold.
          charms:      (i.charms || []).filter(c => c.name),
          buyPrice:    price != null ? price.toFixed(2) : '',
          siteFee:     platformSite || settings?.sites?.[0] || null,
          timestamp:   new Date().toISOString(),
        };
      });
    onImport(toImport);
    onClose();
  };

  const setAction = (key, updates) =>
    setChangeActions(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...updates } }));

  const allHandled = changes.every(c => changeActions[c.assetId || c.id]?.action != null);
  const syncLabel  = meta?.syncedAt
    ? new Date(meta.syncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  // ── Render ─────────────────────────────────────────────
  return (
    <div className={`mdl-overlay top ${variant}`} onClick={onClose}>
      <div className="mdl-panel wide" onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label="Import from Steam inventory">

        {/* ── Header ────────────────────────────────── */}
        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">
              {view === 'changes' ? `Changes Detected (${changes.length} item${changes.length !== 1 ? 's' : ''})` : 'Import from Steam Inventory'}
            </div>
            <div className="mdl-sub">
              {view === 'changes'
                ? 'Items missing since last check. Mark each as sold or traded.'
                : 'Select items to add to your tracker'}
            </div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Steam ID row (always visible) ─────────── */}
        <div className="iim-idbar">
          <div className="iim-idbar-field">
            <span className="mdl-label">Steam64 ID</span>
            <input type="text" className="mdl-input" value={steamId} onChange={e => setSteamId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doFetch(false)}
              placeholder="76561198XXXXXXXXX" />
          </div>
          <button className="mdl-btn primary" onClick={() => doFetch(false)} disabled={view === 'loading'}>
            {view === 'loading' ? 'Loading…' : 'Fetch'}
          </button>
          {view !== 'idle' && view !== 'loading' && (
            <button className="mdl-btn" onClick={() => doFetch(true)} disabled={view === 'loading'}
              title="Force re-fetch from Steam">
              Force Refresh
            </button>
          )}
        </div>

        {/* ── Content area ─────────────────────────── */}
        <div className="mdl-body">

          {/* ── Error ──────────────────────────────── */}
          {error && (
            <div className="mdl-card warn" style={{ marginBottom: 12 }}>
              <div className="mdl-card-title mdl-bad">{error}</div>
            </div>
          )}

          {/* Idle */}
          {view === 'idle' && !error && (
            <div className="mdl-blank">
              <div className="mdl-blank-title">Enter your Steam64 ID and click Fetch</div>
              <div className="mdl-blank-sub">Make sure your Steam inventory is set to Public</div>
            </div>
          )}

          {/* Loading */}
          {view === 'loading' && (
            <div className="mdl-blank">
              <div className="mdl-blank-title">Fetching from Steam…</div>
            </div>
          )}

          {/* Changes view */}
          {view === 'changes' && (
            <div>
              <div className="mdl-card-hint" style={{ marginBottom: 12 }}>
                These items were in your inventory last time but are gone now. Tell us what happened:
              </div>
              {changes.map(change => (
                <ChangeCard
                  key={change.assetId || change.id}
                  change={change}
                  action={changeActions[change.assetId || change.id] || {}}
                  onAction={setAction}
                  settings={settings}
                  searchableOptions={searchableOptions}
                  inventoryItems={items}
                />
              ))}
            </div>
          )}

          {/* Grid view */}
          {view === 'grid' && (
            <>
              {/* Price source + meta row */}
              <div className="iim-toolbar">
                <span className="iim-count">Buy price from:</span>
                {[
                  { id: 'buff',    label: 'Buff163',  avail: !!livePrices },
                  { id: 'csfloat', label: 'CSFloat',  avail: !!csfloatPrices },
                  { id: 'none',    label: 'No price', avail: true },
                ].map(src => (
                  <button key={src.id} className={`mdl-pill${priceSource === src.id ? ' on' : ''}`}
                    disabled={!src.avail} onClick={() => setPriceSource(src.id)}>
                    {src.label}{!src.avail ? ' (loading)' : ''}
                  </button>
                ))}
                <FetchFloatsButton items={items} selected={selected} onFloatsFetched={setFloatOverrides} />
                <span className="iim-count iim-toolbar-end">
                  <strong>{items.length}</strong> items
                  {meta?.cached && syncLabel && <span> · cached {syncLabel}</span>}
                </span>
              </div>

              {/* Search + filter */}
              <div className="iim-toolbar">
                <input type="text" className="mdl-input iim-search" value={search}
                  onChange={e => setSearch(e.target.value)} placeholder="Search items…" />
                <div className="iim-acts">
                  <button className={`mdl-pill${filterExt === '' ? ' on' : ''}`}
                    onClick={() => setFilterExt('')}>All</button>
                  {availableExteriors.map(ext => (
                    <button key={ext} className={`mdl-pill${filterExt === ext ? ' on' : ''}`}
                      onClick={() => setFilterExt(ext === filterExt ? '' : ext)}>
                      {extShort(ext)}
                    </button>
                  ))}
                </div>
                <div className="iim-acts iim-toolbar-end">
                  <button className="mdl-pill" onClick={() => setSelected(new Set(filteredItems.filter(i => !isTracked(i)).map(i => i.assetId)))}>Select All</button>
                  <button className="mdl-pill" onClick={() => setSelected(new Set())}>Clear</button>
                </div>
              </div>

              {/* Item grid */}
              {items.length === 0 ? (
                <div className="mdl-blank"><div className="mdl-blank-title">No items found</div></div>
              ) : filteredItems.length === 0 ? (
                <div className="mdl-blank"><div className="mdl-blank-title">No items match the filter</div></div>
              ) : (
                <div className="iim-grid">
                  {filteredItems.map(item => {
                    const isSel     = selected.has(item.assetId);
                    const tracked   = isTracked(item);
                    const price     = getPrice(item);
                    const baseName  = stripCondition(item.market_hash_name);
                    // No rarity colour: leave the strip unstyled so each skin's
                    // own fallback shows through instead of a white wash.
                    const rarityColor = item.rarityColor || null;

                    return (
                      <div key={item.assetId}
                        className={`iim-tile${isSel ? ' on' : ''}${tracked ? ' taken' : ''}`}
                        onClick={() => {
                          if (tracked) return;
                          setSelected(prev => {
                            const n = new Set(prev);
                            n.has(item.assetId) ? n.delete(item.assetId) : n.add(item.assetId);
                            return n;
                          });
                        }}>
                        <div className="iim-rarity" style={rarityColor ? { background: rarityColor } : undefined} />

                        {/* Checkbox */}
                        {!tracked && <div className="iim-check">{isSel && '✓'}</div>}
                        {/* Already tracked badge */}
                        {tracked && <div className="iim-tracked">tracked</div>}

                        {/* Image */}
                        <div className="iim-img">
                          {item.icon_url
                            ? <img src={item.icon_url} alt="" loading="lazy" />
                            : <div className="iim-img-blank" />
                          }
                        </div>

                        {/* Info */}
                        <div className="iim-info">
                          <div className="iim-name">{baseName}</div>
                          {item.exterior && <div className="iim-ext">{item.exterior}</div>}
                          {price != null && <div className="iim-price">${price.toFixed(2)}</div>}
                          {(item.stickers?.length > 0 || item.charms?.length > 0) && (
                            <div className="iim-stickers">
                              {(item.stickers || []).map((s, si) => {
                                const sp = getPrice({ market_hash_name: 'Sticker | ' + s.name });
                                return (
                                  <div key={si} className="iim-sticker"
                                    title={s.name + (sp != null ? ` ($${sp.toFixed(2)})` : '')}>
                                    {s.image
                                      ? <img src={s.image} alt="" loading="lazy" />
                                      : <div className="iim-sticker-blank" />
                                    }
                                    {sp != null && (
                                      <span className="iim-sticker-px">
                                        ${sp < 10 ? sp.toFixed(2) : sp.toFixed(0)}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              {(item.charms || []).map((c, ci) => {
                                const cp = getPrice({ market_hash_name: c.name });
                                return (
                                  <div key={`c${ci}`} className="iim-sticker iim-charm"
                                    title={c.name + (cp != null ? ` ($${cp.toFixed(2)})` : '')}>
                                    {c.image
                                      ? <img src={c.image} alt="" loading="lazy" />
                                      : <div className="iim-sticker-blank" />
                                    }
                                    {cp != null && (
                                      <span className="iim-sticker-px">
                                        ${cp < 10 ? cp.toFixed(2) : cp.toFixed(0)}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ───────────────────────────────── */}
        <div className="mdl-foot">
          {view === 'grid' && selected.size > 0 && (
            <span className="mdl-foot-note"><strong>{selected.size}</strong> selected</span>
          )}
          <button className="mdl-btn" onClick={onClose}>Cancel</button>

          {view === 'changes' && (
            <button className="mdl-btn primary wide" onClick={confirmChanges} disabled={!allHandled}>
              Continue to Import →
            </button>
          )}

          {view === 'grid' && (
            <button className="mdl-btn primary wide" onClick={handleImport} disabled={selected.size === 0}>
              {selected.size > 0 ? `Import ${selected.size} Item${selected.size !== 1 ? 's' : ''}` : 'Import Items'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────
function extShort(ext) {
  return { 'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field-Tested': 'FT',
    'Well-Worn': 'WW', 'Battle-Scarred': 'BS' }[ext] || ext;
}
